import type { ComponentProps, ReactNode } from 'react'
import { ClipboardCheckIcon, ClipboardCopyIcon } from 'lucide-react'
import { cn, findLeafPrimitiveValue, tv } from '@joyful/utils'

import { Slot } from './Slot'
import { Tooltip } from './Tooltip'
import { useCopyToClipboard } from './useCopyToClipboard'
import { useRecentlyCopiedFeedback } from './useRecentlyCopiedFeedback'

export interface CopyableProps extends Omit<
  ComponentProps<'span'>,
  'className'
> {
  /**
   * @default false
   */
  asChild?: boolean

  children: ReactNode

  /**
   * if provided, will be used instead of children
   */
  value?: string
}

export const interactiveTextVariants = tv({
  base: 'rounded-xs transition-[background-color,box-shadow]',

  defaultVariants: {
    disabled: false,
    isActive: false,
  },

  variants: {
    disabled: {
      false: 'cursor-pointer',
    },

    isActive: {
      false:
        'hover:bg-gray-200 dark:hover:bg-tremor-background-subtle hover:shadow-[0_0_0_2px_var(--tremor-border)] data-[state=open]:bg-gray-200 dark:data-[state=open]:bg-tremor-background-subtle',
      true: 'hover:bg-blue-200 dark:hover:bg-blue-900/60 hover:shadow-[0_0_0_2px_var(--tremor-border)] data-[state=open]:bg-blue-200 dark:data-[state=open]:bg-blue-900/60',
    },
  },
})

export function Copyable({
  asChild = false,
  children,
  value,
  ...props
}: CopyableProps) {
  const { didRecentlyCopy, isTooltipOpen, markCopied, setIsTooltipOpen } =
    useRecentlyCopiedFeedback()
  const valueToCopy =
    value ?? findLeafPrimitiveValue(children)?.toString() ?? ''
  const [, copyText] = useCopyToClipboard({
    onSuccess: () => {
      markCopied()
    },
  })

  // SAFETY: Slot parents (e.g. Redact asChild) inject className at runtime onto CopyableProps which omits it.
  const { className: injectedClassName, ...domProps } =
    props as CopyableProps & {
      className?: string
    }

  const Component = asChild ? Slot : 'span'
  const content = (
    <Component
      className={cn(
        valueToCopy !== '' &&
          interactiveTextVariants({
            disabled: copyText.isPending || didRecentlyCopy,
          }),
        injectedClassName,
      )}
      {...domProps}
      onClick={(event) => {
        event.stopPropagation()
        event.preventDefault()

        copyText.mutate(valueToCopy)
        props.onClick?.(event)
      }}
    >
      {children}
    </Component>
  )

  if (valueToCopy === '') {
    return content
  }

  const CopyIcon = didRecentlyCopy ? ClipboardCheckIcon : ClipboardCopyIcon
  return (
    <Tooltip
      contentProps={{
        closeAnimation: 'instant',
      }}
      tooltipRootProps={{
        onOpenChange: setIsTooltipOpen,
        open: isTooltipOpen,
      }}
      content={
        <span className="inline-flex items-center gap-1 text-xs">
          <CopyIcon className="size-3" />
          {didRecentlyCopy ? 'Copied!' : `Copy to clipboard`}
        </span>
      }
    >
      {content}
    </Tooltip>
  )
}
